import * as React from 'react';
import * as Icons from 'lucide-react';
import { HelpCircle, type LucideIcon } from 'lucide-react';

export interface EmptyStateProps {
  icon: string; // lucide icon name (kebab-case or PascalCase)
  title: string;
  body: string;
  action?: () => void;
  actionLabel?: string;
}

// Resolve a lucide icon name (kebab-case per design files, e.g. "check-circle")
// to its PascalCase exported component ("CheckCircle"). Falls back to HelpCircle.
function resolveIcon(name: string): LucideIcon {
  const pascal = name
    .replace(/(^|[-_\s])(\w)/g, (_m, _sep, ch: string) => ch.toUpperCase());
  const reg = Icons as unknown as Record<string, LucideIcon>;
  return reg[pascal] ?? reg[name] ?? HelpCircle;
}

// Per design spec (04-global-components.html §3 + README §4d).
export function EmptyState({ icon, title, body, action, actionLabel }: EmptyStateProps) {
  const IconCmp = resolveIcon(icon);
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '60px 40px',
        textAlign: 'center',
      }}
    >
      <div
        style={{
          width: 52,
          height: 52,
          borderRadius: 14,
          background: 'var(--bg-muted)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: 16,
        }}
      >
        <IconCmp size={24} color="var(--text-muted)" data-testid="empty-state-icon" />
      </div>
      <div
        style={{
          fontSize: 15.5,
          fontWeight: 700,
          color: 'var(--text-primary)',
          marginBottom: 6,
        }}
      >
        {title}
      </div>
      <div
        style={{
          fontSize: 13.5,
          color: 'var(--text-secondary)',
          lineHeight: 1.5,
          maxWidth: 320,
          marginBottom: action ? 20 : 0,
        }}
      >
        {body}
      </div>
      {action && actionLabel && (
        <button
          type="button"
          onClick={action}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '9px 18px',
            borderRadius: 8,
            background: 'var(--accent)',
            color: '#fff',
            border: 'none',
            fontSize: 13,
            fontWeight: 600,
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          {actionLabel}
        </button>
      )}
    </div>
  );
}

export default EmptyState;
